import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { IdentifyResult } from '@okmaps/shared';

interface Props {
  readonly result: IdentifyResult;
  readonly title: string;
}

/**
 * One identified feature.
 *
 * Attribute names come straight from the source data and are usually TIGER
 * column names - `namelsad10`, `countyfp10`. They are shown as published rather
 * than prettified: a GIS professional recognizes them, and inventing friendlier
 * labels would misrepresent what is actually in the dataset.
 *
 * Empty attributes are dropped. A table of twenty blank rows is worse than a
 * short one.
 */
export default function IdentifyResultCard({ result, title }: Props) {
  const entries = Object.entries(result.attributes).filter(
    ([, v]) => v !== null && v !== '' && v !== undefined,
  );

  return (
    <Box sx={{ px: 1.5, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="caption" color="primary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
        {title}
      </Typography>

      {entries.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          This feature has no attributes.
        </Typography>
      ) : (
        <Box
          component="dl"
          sx={{
            m: 0,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)',
            columnGap: 1,
            rowGap: 0.4,
          }}
        >
          {entries.map(([key, value]) => (
            <Box key={key} sx={{ display: 'contents' }}>
              <Typography
                component="dt"
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.6875rem', overflowWrap: 'anywhere' }}
              >
                {key}
              </Typography>
              <Typography component="dd" variant="caption" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                {String(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
